import { filter, get, pick } from "lodash";
import { onlineT } from "../../../typings/online";
import { paymentT } from "../../../typings/payment";
import { thor } from "../../../typings/thor";
import { Enums, Events } from "../../../thor.enums";
import { LocaleCode } from "../../../typings/lang";

export class OnlineItemEditController {
    public static $inject: string[] = [
        "$log",
        "$mdDialog",
        "$mdMedia",
        "$rootScope",
        "$scope",
        "$translate",
        "hotkeys",
        "onlineContainerEditModel",
        "onlineItemEdit",
        "itemService",
        "mediaGalleryService",
        "onlinePriceService",
        "toaster",
        "categoryService",
        "paymentService",
        "utilsService"
    ];

    // binded
    public getTranslationStatus: Function;
    public isSaving: boolean;
    public readOnly: boolean;
    public saleCurrency: Enums.Currency;
    public selectedTargetLanguage: LocaleCode;
    public sourceLang: LocaleCode;

    public categories: any[];
    public fields: Array<any> = [];
    public form;
    public originalItem;
    public showTooltip: boolean = false;
    public lastModifiedDate: Date;
    public isItemSold: boolean = false;
    public pastItemStatusEnum = onlineT.past.ItemStatus;
    public linkPaymentStates: paymentT.thor.LinkPaymentState[];
    public filteredItemsCount = 0;

    private patchedData: thor.ItemPatch = {};

    private $t;

    constructor(
        private $log: ng.ILogService,
        private $mdDialog: ng.material.IDialogService,
        public $mdMedia: ng.material.IMedia,
        private $rootScope: ng.IRootScopeService,
        $scope: ng.IScope,
        $translate: ng.translate.ITranslateService,
        private hotkeys,
        public saleEditModel,
        public itemEdit,
        private itemService,
        private mediaGalleryService,
        private onlinePriceService,
        private toaster,
        categoryService,
        private paymentService,
        private utilsService
    ) {
        this.$t = $translate.instant;

        $scope.$watch(
            () => {
                return this.itemEdit.isOnlineOpen;
            },
            isOnlineOpen => {
                if (isOnlineOpen) {
                    this.fields = categoryService.getFields();
                    this.categories = categoryService.getCategories();
                    this._autoFocus();
                    this._bindHotkeys();
                    this.checkEstimateValidity();
                    this.filteredItemsCount = this.getFilteredItemsCount();
                    this._initPayment();
                    this.lastModifiedDate = new Date(get(this.itemEdit.currentItem, "last_updated"));
                    return;
                }
                this._unbindHotkeys();
            }
        );
    }

    public calculateFinalPrice(price: number) {
        return this.onlinePriceService.calculateFinalPrice(
            price,
            this.itemEdit.commissionRates,
            get(this.itemEdit, "currentItem.category.field"),
            { currency: this.saleCurrency, start_at: this.saleEditModel.resource?.start_at }
        );
    }

    public getCommissionRatesLabel(price: number) {
        return this.onlinePriceService.getCommissionRatesLabel(
            price,
            this.itemEdit.commissionRates,
            get(this.itemEdit, "currentItem.category.field"),
            { currency: this.saleCurrency, start_at: this.saleEditModel.resource?.start_at }
        );
    }

    public checkEstimateValidity() {
        if (!this.form) {
            return;
        }
        const min = get(this.itemEdit.currentItem, "pricing.estimates.min");
        const max = get(this.itemEdit.currentItem, "pricing.estimates.max");

        this.form.estimateMin.$setValidity("estimateEmpty", (!min && !max) || !!min);
        this.form.estimateMax.$setValidity("estimateEmpty", (!min && !max) || !!max);
        this.form.estimateMax.$setValidity("estimateValue", !min || !max || min <= max);

        this.patchPricing();
        this.checkReservePriceValidity();
    }

    public checkReservePriceValidity() {
        const estimateMin = get(this.itemEdit.currentItem, "pricing.estimates.min");
        const reservePrice = get(this.itemEdit.currentItem, "pricing.reserve_price");
        const startingPrice = get(this.itemEdit.currentItem, "pricing.starting_price");

        this.form.reservePrice.$setValidity(
            "reservePriceEstimateValue",
            !estimateMin || !reservePrice || estimateMin >= reservePrice
        );
        this.form.reservePrice.$setValidity(
            "reservePriceStartingValue",
            !startingPrice || !reservePrice || startingPrice <= reservePrice
        );

        this.patchPricing();
    }

    public canSave() {
        if (angular.equals(this.patchedData, {})) {
            return false;
        }
        return !(this.isSaving || this.form.$invalid);
    }

    public close() {
        this.showTooltip = false;

        if (this.itemEdit.isPast || angular.equals(this.patchedData, {})) {
            this.itemEdit.resetCurrentItem();
            this.itemEdit.close();
        } else {
            const confirm = this.$mdDialog
                .confirm()
                .title(this.$t("fermer_sans_sauvegarder"))
                .textContent(this.$t("ce_lot_a_ete_modifie_souhaitez_vous_fermer_sans_sauvegarder_les_modifications"))
                .ariaLabel(this.$t("fermer_un_lot_non_sauvegarde"))
                .ok(this.$t("oui"))
                .cancel(this.$t("annuler"));
            this.$mdDialog.show(confirm).then(() => {
                this.patchedData = {};
                this.itemEdit.resetCurrentItem();
                this.itemEdit.close();
            });
        }
    }

    public getFilteredItemsCount() {
        return this.itemEdit.getFilteredItems().length;
    }

    public next() {
        if (this.itemEdit.nextItem) {
            if (this.itemEdit.isPast) {
                this.itemEdit.next();
                this._initPayment();
                this._autoFocus();
            } else {
                this.save().then(() => {
                    this.itemEdit.next();
                    this._autoFocus();
                });
            }
        }
    }

    public previous() {
        if (this.itemEdit.previousItem) {
            if (this.itemEdit.isPast) {
                this.itemEdit.previous();
                this._initPayment();
                this._autoFocus();
            } else {
                this.save().then(() => {
                    this.itemEdit.previous();
                    this._autoFocus();
                });
            }
        }
    }

    public patchCategory() {
        const currentCategory = get(this.itemEdit.currentItem, "category.id");
        const originalCategory = get(this.itemEdit.originalItem, "category.id");

        if (angular.equals(currentCategory, originalCategory)) {
            delete this.patchedData.category_id;
        } else {
            this.patchedData.category_id = currentCategory;
        }
    }

    public patchDescription() {
        const currentDescription = get(this.itemEdit.currentItem, "description");
        const originalDescription = get(this.itemEdit.originalItem, "description");

        if (angular.equals(currentDescription, originalDescription)) {
            delete this.patchedData.description;
        } else {
            this.patchedData.description = currentDescription;
        }
    }

    public patchDescriptionTranslation() {
        if (
            angular.equals(
                this.itemEdit.originalItem.description_translations[this.selectedTargetLanguage],
                this.itemEdit.currentItem.description_translations[this.selectedTargetLanguage]
            )
        ) {
            delete this.patchedData.description_translations;
        } else {
            this.patchedData.description_translations = {
                [this.selectedTargetLanguage]:
                    this.itemEdit.currentItem.description_translations[this.selectedTargetLanguage]
            };
        }
    }

    public patchInternalReference() {
        const currentInternalRef = get(this.itemEdit.currentItem, "internal_reference");
        const originalInternalRef = get(this.itemEdit.originalItem, "internal_reference");

        if (angular.equals(currentInternalRef, originalInternalRef)) {
            delete this.patchedData.internal_reference;
        } else {
            this.patchedData.internal_reference = currentInternalRef;
        }
    }

    public patchOrderNumber() {
        const originalMeta: thor.ItemMeta = <any>pick(this.itemEdit.originalItem.meta, "order_number");
        const currentMeta: thor.ItemMeta = <any>pick(this.itemEdit.currentItem.meta, "order_number");
        const items: Array<thor.ItemGet> = filter(
            angular.copy(this.itemEdit.items),
            (item: thor.ItemGet) => item.id !== this.itemEdit.currentItem.id
        );
        const sameItemFound = this.utilsService.findItemByOrderNumber(currentMeta.order_number, items);

        if (this.form.orderNumberPrimary) {
            this.form.orderNumberPrimary.$setValidity("uniqueOrderNumber", !sameItemFound);
        }

        if (angular.equals(currentMeta, originalMeta) || sameItemFound || !get(currentMeta, "order_number.primary")) {
            return (this.patchedData.meta = null);
        }
        this.patchedData.meta = currentMeta;
    }

    public patchPricing() {
        const currentItemPricing = get(this.itemEdit, "currentItem.pricing");
        const originalItemPricing = get(this.itemEdit, "originalItem.pricing");

        if (angular.equals(originalItemPricing, currentItemPricing)) {
            delete this.patchedData.pricing;
        } else {
            if (typeof this.patchedData.pricing === "undefined") {
                this.patchedData.pricing = {};
            }
            this.patchedData.pricing = {
                starting_price: get(this.itemEdit.currentItem, "pricing.starting_price", 0),
                reserve_price: get(this.itemEdit.currentItem, "pricing.reserve_price", null),
                caution: get(this.itemEdit.currentItem, "pricing.caution", null),
                estimates: {
                    min: get(this.itemEdit.currentItem, "pricing.estimates.min"),
                    max: get(this.itemEdit.currentItem, "pricing.estimates.max")
                }
            };
        }
    }

    public saveAndClose() {
        this.showTooltip = false;

        if (!this.canSave()) {
            return this.close();
        }
        this.save().then(() => {
            this.itemEdit.close();
        });
    }

    public async save() {
        if (this.form.$invalid) {
            return;
        }
        this.isSaving = true;

        return this._saveItem()
        .then(() => {
            if (!angular.equals(this.itemEdit.currentItem, this.itemEdit.originalItem)) {
                this.toaster.toast({
                    message: this.$t("lot_sauvegarde"),
                    delay: 2000
                });

                const fetchItems = !this.utilsService.areOrdersNumbersEquals(
                    this.itemEdit.currentItem,
                    this.itemEdit.originalItem
                );
                this.$rootScope.$broadcast(Events.OnlineItemUpdated, { fetchItems });
                this.itemEdit.originalItem = angular.copy(this.itemEdit.currentItem);
            }
        })
        .finally(() => {
            this.patchedData = {};
            this.isSaving = false;
        });
    }

    public showBuyerInfos() {
        return (
            this.itemEdit.currentItem.pricing.last_bid &&
            this.itemEdit.currentItem.paymentStatus !== onlineT.past.ItemStatus.CANCELED
        );
    }

    public showGallery() {
        this.mediaGalleryService.open(this.itemEdit.currentItem, "gallery", this.readOnly);
    }

    public getCanceledLabel() {
        const label = this.$t("date_a", {
            DATE: this.lastModifiedDate.toLocaleString("fr-FR", {
                weekday: "long",
                day: "numeric",
                month: "short",
                year: "numeric"
            }),
            TIME: this.lastModifiedDate.toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit" })
        });
        return label.charAt(0).toUpperCase() + label.slice(1);
    }

    public isPaymentDone() {
        const payments = get(this.itemEdit, "currentItem.payment");
        if (!payments) {
            return;
        }
        return payments.find(elem => {
            return (
                elem.type === paymentT.thor.LinkPaymentStateType.ITEM &&
                elem.nature === paymentT.PaymentNature.INTEGRAL_PAYMENT &&
                get(elem, "link_payment.status") === paymentT.LinkPaymentStatus.SUCCESS
            );
        });
    }

    public getCountryName() {
        const country = this.itemEdit.currentItem.pricing.last_bid?.location?.country;
        if (country) {
            return this.$t(country);
        }
    }

    public isReadOnly() {
        return !this.itemEdit.isEdited && this.readOnly;
    }

    private async _saveItem() {
        try {
            let updatedTranslations = {};

            if (this.patchedData.description_translations) {
                updatedTranslations = await this.handleDescriptionTranslations();

                if (this.patchedData.description_translations) {
                    delete this.patchedData.description_translations;
                }
            }

            if (!angular.equals(this.patchedData, {})) {
                if (get(this.patchedData, "pricing.reserve_price") === 0) {
                    this.patchedData.pricing.reserve_price = null;
                }

                const res = await this.itemService.update(this.itemEdit.currentItem.id, this.patchedData);
                this.$log.info("Item saved ✓", res);
                await this.getTranslationStatus();
                if (!angular.equals(updatedTranslations, {})) {
                    this.itemEdit.currentItem.description_translations = updatedTranslations;
                }
                this.itemEdit.currentItem = (<any>Object).assign(this.itemEdit.currentItem, res);
            }
        } catch (error) {
            this.$log.error("Error while updating the item", error);
            this.toaster.show(this.$t("une_erreur_est_survenue"), true);
        }
    }

    private _initPayment() {
        this.isItemSold =
            this.itemEdit.currentItem.paymentStatus === this.pastItemStatusEnum.UNPAID ||
            this.itemEdit.currentItem.paymentStatus === this.pastItemStatusEnum.PAID;
        const payments = get(this.itemEdit, "currentItem.payment");
        this.linkPaymentStates = payments ? this.paymentService.getLinkPaymentStates(payments, this.saleCurrency) : [];
    }

    private handleDescriptionTranslations() {
        const descriptionTranslations = {
            description_translations: this.patchedData.description_translations
        };
        this.isSaving = true;

        return this.itemService
            .translate(this.itemEdit.currentItem.id, descriptionTranslations)
            .then(async res => {
                this.$log.info("Item translation saved ✓", res);
                await this.getTranslationStatus();
                return res.description_translations;
            })
            .finally(() => (this.isSaving = false));
    }

    private _autoFocus() {
        setTimeout(() => {
            angular.element(document.querySelector("input[auto-focus]")).focus();
        }, 200);
    }

    private _bindHotkeys() {
        this.hotkeys.add({
            combo: "mod+down",
            description: this.$t("lot_suivant"),
            allowIn: ["INPUT", "SELECT", "TEXTAREA"],
            callback: e => {
                e.preventDefault();
                this.save().then(() => {
                    this.next();
                });
            }
        });

        this.hotkeys.add({
            combo: "mod+up",
            description: this.$t("lot_precedent"),
            allowIn: ["INPUT", "SELECT", "TEXTAREA"],
            callback: e => {
                e.preventDefault();
                this.save().then(() => {
                    this.previous();
                });
            }
        });

        this.hotkeys.add({
            combo: "mod+s",
            description: this.$t("sauvegarde_le_lot"),
            allowIn: ["INPUT", "SELECT", "TEXTAREA"],
            callback: e => {
                e.preventDefault();
                this.save();
            }
        });

        this.hotkeys.add({
            combo: "esc",
            description: this.$t("ferme_le_mode_edition"),
            allowIn: ["INPUT", "SELECT", "TEXTAREA"],
            callback: e => {
                e.preventDefault();
                this.close();
            }
        });
    }

    private _unbindHotkeys() {
        this.hotkeys.del("mod+up");
        this.hotkeys.del("mod+down");
        this.hotkeys.del("mod+s");
        this.hotkeys.del("esc");
    }
}
